import type { DigestSendResult, TestEmailResult } from "../../convex/model/emailUpdates";
import type { ChangeType } from "../../convex/model/enums";
import { cleanNullableText } from "./text";

export type EmailUpdateNotice = {
  readonly tone: "success" | "warning";
  readonly text: string;
};

export type EmailUpdateNoticeTone = EmailUpdateNotice["tone"];

export type EmailUpdateSendWorkflowState = {
  readonly sending: boolean;
  readonly testSending: boolean;
  readonly notice: EmailUpdateNotice | null;
};

export const emptyEmailUpdateSendWorkflowState = (): EmailUpdateSendWorkflowState => ({
  sending: false,
  testSending: false,
  notice: null,
});

export type EmailUpdateSendWorkflowAction =
  | { readonly type: "digestStarted" }
  | { readonly type: "testStarted" }
  | { readonly type: "sendFinished"; readonly notice: EmailUpdateNotice }
  | { readonly type: "sendFailed"; readonly message: string };

export const emailUpdateFailureNotice = (message: string): EmailUpdateNotice => ({
  tone: "warning",
  text: message,
});

export const nextEmailUpdateSendWorkflowState = (
  _current: EmailUpdateSendWorkflowState,
  action: EmailUpdateSendWorkflowAction,
): EmailUpdateSendWorkflowState => {
  switch (action.type) {
    case "digestStarted":
      return { sending: true, testSending: false, notice: null };
    case "testStarted":
      return { sending: false, testSending: true, notice: null };
    case "sendFinished":
      return { sending: false, testSending: false, notice: action.notice };
    case "sendFailed":
      return { sending: false, testSending: false, notice: emailUpdateFailureNotice(action.message) };
  }
};

export type EmailUpdateSendState = {
  readonly sending: boolean;
  readonly testSending: boolean;
  readonly companyCount: number;
};

export type EmailUpdateTestSendState = Pick<EmailUpdateSendState, "sending" | "testSending">;

export type EmailUpdateDigestButtonInput = EmailUpdateSendState & {
  readonly entryCount: number;
};

export type EmailUpdateButtonView = {
  readonly disabled: boolean;
  readonly label: string;
};

export type {
  DigestSendReason,
  DigestSendResult,
  TestEmailReason,
  TestEmailResult,
} from "../../convex/model/emailUpdates";

const CHANGE_TYPE_LABELS = {
  amended: "Amendments",
  insertion: "Insertions",
  repeal: "Repealed",
} satisfies Record<ChangeType, string>;

const NOTICE_TONE_CLASS: Record<EmailUpdateNoticeTone, string> = {
  success: "border-emerald-200 bg-emerald-50 text-emerald-800",
  warning: "border-amber-200 bg-amber-50 text-amber-800",
};

const RECIPIENT_TONE_CLASS = {
  ready: "bg-emerald-50 font-medium text-emerald-700 ring-emerald-200",
  missingRecipients: "bg-amber-50 font-medium text-amber-700 ring-amber-200",
} as const;

export const emailUpdateChangeTypeLabel = (changeType: ChangeType): string =>
  CHANGE_TYPE_LABELS[changeType];

export const emailUpdateChangeCountText = (count: number): string =>
  `${count} ${plural(count, "change", "changes")}`;

export const emailUpdateRecipientCountText = (count: number): string =>
  `${count} ${plural(count, "recipient", "recipients")}`;

export const canSendTestEmailUpdate = ({ sending, testSending }: EmailUpdateTestSendState): boolean =>
  !sending && !testSending;

export const canSendDigestEmailUpdate = ({ sending, testSending, companyCount }: EmailUpdateSendState): boolean =>
  !sending && !testSending && companyCount > 0;

export const emailUpdateTestButtonView = (
  state: EmailUpdateTestSendState,
): EmailUpdateButtonView => ({
  disabled: !canSendTestEmailUpdate(state),
  label: state.testSending ? "Testing..." : "Send test",
});

export const emailUpdateDigestButtonView = ({
  entryCount,
  ...state
}: EmailUpdateDigestButtonInput): EmailUpdateButtonView => ({
  disabled: !canSendDigestEmailUpdate(state),
  label: state.sending ? "Sending..." : `Send updates (${entryCount})`,
});

export const emailUpdateNoticeToneClass = (tone: EmailUpdateNoticeTone): string =>
  NOTICE_TONE_CLASS[tone];

export const emailUpdateRecipientToneClass = (recipientCount: number): string =>
  recipientCount > 0 ? RECIPIENT_TONE_CLASS.ready : RECIPIENT_TONE_CLASS.missingRecipients;

export const digestSendNotice = (result: DigestSendResult): EmailUpdateNotice => {
  switch (result.reason) {
    case "sent":
    case "sent_with_skips":
      return {
        tone: "success",
        text:
          `Sent ${result.sent} ${plural(result.sent, "digest", "digests")}` +
          (result.skipped > 0
            ? ` and skipped ${result.skipped} ${plural(result.skipped, "company", "companies")} with no recipients`
            : "") +
          ". Successful deliveries were recorded and will not be resent.",
      };
    case "missing_provider":
      return {
        tone: "warning",
        text: "No email provider is configured. Nothing was sent.",
      };
    case "no_recipients":
      return {
        tone: "warning",
        text: "No active client recipients were found. Staff users do not receive client digests, so these updates remain pending.",
      };
    case "nothing_pending":
      return { tone: "success", text: "No pending updates to send." };
    case "partial_failure":
      return {
        tone: "warning",
        text:
          `Resend accepted ${result.sent} of ${result.companies} ${plural(result.companies, "digest", "digests")}; ` +
          `${result.failed} failed and ${result.skipped} had no recipients. Successful deliveries were recorded; the rest remain pending.`,
      };
  }
};

export const testEmailNotice = (result: TestEmailResult): EmailUpdateNotice => {
  switch (result.reason) {
    case "sent":
      return {
        tone: "success",
        text: `Test email accepted by Resend for ${result.recipient ?? "the current staff address"}. Check that inbox and spam folder.`,
      };
    case "missing_provider":
      return {
        tone: "warning",
        text: "No email provider is configured. Set RESEND_API_KEY before sending emails.",
      };
    case "no_staff_email":
      return {
        tone: "warning",
        text: "Your staff user does not have an email address to receive a test message.",
      };
    case "provider_rejected": {
      const status = result.status === null ? "" : ` with status ${result.status}`;
      const error = cleanNullableText(result.error);
      return {
        tone: "warning",
        text: `Resend rejected the test email${status}${error === null ? "." : `: ${error}`}`,
      };
    }
  }
};

const plural = (count: number, singular: string, pluralText: string): string =>
  count === 1 ? singular : pluralText;

